import React from "react";
import Layout from "../../components/Layout";
import FinalCtaPanel from "../../components/FinalCtaPanel";
import "../../styles/brand.css";
import TrainingHero from "../trainings/TrainingHero";
import ImageBanner from "../../components/ImageBanner";
import IsoCourseGrid from "./IsoCourseGrid";
import { BookOpenIcon } from "./isoIcons";
import { trainingCtaContent } from "./isoTrainingCta";
import {
  categoryToId,
  isoLeadAuditorCourses,
} from "./isoLeadAuditorData";

/* Categories with a supplied banner image show it, as supplied, in place of
   the shared text hero; the others keep the hero. */
const categoryBanner = (file) => ({
  src: `/assets/banners/${file}`,
  width: 1767,
  height: 592,
  whole: true, // shown in full, never cropped
});
const CATEGORY_BANNERS = {
  Quality: categoryBanner("Quality-Lead-Auditor-Trainings.jpeg"),
  "Food Safety": categoryBanner("Food-Safety-Services.jpeg"),
  "Health and Security": categoryBanner("Health-and-Security.jpeg"),
  "Business Excellence": categoryBanner("Business-Excellence.jpeg"),
  "Product / Process Certification": categoryBanner("Product-Process-Certification.jpeg"),
  "Information Security": categoryBanner("Information-Security.jpeg"),
};

/* ============================================================================
   One Services dropdown item = one page.

   Every page in the set is THIS component with a different `category`, so the
   three parts stay in lockstep by construction:

     shared banner (TrainingHero)  ->  this category's courses  ->  shared CTA

   Only the middle changes. Nothing here filters at render time on a hash or a
   tab — the route decides the category and the grid is given only that
   category, so a page cannot show another service's courses.
   ========================================================================= */

const SITE_URL = "https://mbasolutions.co";

const ServiceCategoryPage = ({ category }) => {
  const slug = categoryToId(category);
  const courses = isoLeadAuditorCourses.filter((c) => c.category === category);
  const canonical = `${SITE_URL}/services/${slug}`;

  return (
    <Layout
      title={`${category} Lead Auditor Training | MBA Solutions`}
      description={`${courses.length} internationally recognized ${category} lead auditor certification training programs from MBA Solutions.`}
      keywords={`${category} lead auditor training, ${category} certification, ISO training, MBA Solutions`}
      canonical={canonical}
      jsonLd={[
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${category} Lead Auditor Trainings`,
          url: canonical,
          mainEntity: {
            "@type": "ItemList",
            itemListElement: courses.map((course, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: `${course.iso} — ${course.title}`,
            })),
          },
        },
      ]}
    >
      <main className="brand-page bg-white">
        {/* shared banner — the same component and the same props shape on every
            page in the set; only the words name the service */}
        {CATEGORY_BANNERS[category] ? (
          <ImageBanner
            {...CATEGORY_BANNERS[category]}
            alt={`${category} Services — MBA Solutions`}
            heading={`${category} Lead Auditor Trainings`}
          />
        ) : (
          <TrainingHero
            badge="Internationally Recognized Programs"
            title={`${category} Lead Auditor Trainings`}
            subtitle={`${courses.length} internationally recognized ${category.toLowerCase()} certification training programs designed for professionals and consultants.`}
            icon={BookOpenIcon}
            primaryCta={{ label: "Contact Us", to: "/contact" }}
            secondaryCta={{ label: "All Training", to: "/training" }}
          />
        )}

        {/* only this category */}
        <IsoCourseGrid categories={[category]} headingStyle="centered" />

        {/* shared final section */}
        <FinalCtaPanel
          id={`${slug}-cta-heading`}
          title={trainingCtaContent.title}
          paragraph={trainingCtaContent.paragraph}
          cta={trainingCtaContent.cta}
          image={trainingCtaContent.image}
        />
      </main>
    </Layout>
  );
};

export default ServiceCategoryPage;
